import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowDown, Package, CheckCircle2, XCircle, AlertTriangle, MapPin,
  QrCode, Search, Filter, Clock, User, Truck, FileText, Eye, CheckSquare
} from 'lucide-react';

const EnhancedReceiving = () => {
  const [activeStage, setActiveStage] = useState('staged'); // staged, qc, putaway
  const [selectedReceipt, setSelectedReceipt] = useState(null);

  // Mock data for staged receiving
  const stagedReceipts = [
    {
      id: 1,
      po: 'PO-2024-089',
      supplier: 'Tech Solutions Ltd',
      dock: 'Dock 1',
      items: [
        { sku: 'ELEC-001', name: 'Laptop Dell XPS 15', qty: 50, received: 0, status: 'pending' },
        { sku: 'ELEC-002', name: 'Monitor 27" 4K', qty: 30, received: 0, status: 'pending' },
        { sku: 'ELEC-003', name: 'Keyboard Mechanical', qty: 100, received: 0, status: 'pending' }
      ],
      expectedDate: '2024-01-28',
      expectedTime: '2:00 PM',
      status: 'staged',
      assignedTo: 'John Doe',
      createdAt: '2024-01-28 10:30 AM'
    },
    {
      id: 2,
      po: 'PO-2024-092',
      supplier: 'Office Supplies Co',
      dock: 'Dock 2',
      items: [
        { sku: 'OFF-123', name: 'Printer HP LaserJet', qty: 25, received: 0, status: 'pending' },
        { sku: 'OFF-124', name: 'Paper A4', qty: 500, received: 0, status: 'pending' }
      ],
      expectedDate: '2024-01-28',
      expectedTime: '4:00 PM',
      status: 'staged',
      assignedTo: 'Jane Smith',
      createdAt: '2024-01-28 11:15 AM'
    }
  ];

  // Mock data for QC checkpoints
  const qcReceipts = [
    {
      id: 1,
      po: 'PO-2024-089',
      supplier: 'Tech Solutions Ltd',
      items: [
        { sku: 'ELEC-001', name: 'Laptop Dell XPS 15', qty: 50, received: 50, qcStatus: 'pending', issues: [] },
        { sku: 'ELEC-002', name: 'Monitor 27" 4K', qty: 30, received: 30, qcStatus: 'pending', issues: [] },
        { sku: 'ELEC-003', name: 'Keyboard Mechanical', qty: 100, received: 98, qcStatus: 'pending', issues: ['2 units damaged'] }
      ],
      qcStatus: 'in-progress',
      qcBy: 'Sarah Johnson',
      startedAt: '2024-01-28 2:30 PM'
    }
  ];

  // Mock data for directed put-away
  const putawayTasks = [
    {
      id: 1,
      po: 'PO-2024-089',
      item: { sku: 'ELEC-001', name: 'Laptop Dell XPS 15', qty: 50 },
      suggestedLocation: 'Zone A → Aisle A1 → Rack A1-R1 → Shelf A1-R1-S1 → Bin A1-R1-S1-B3',
      reason: 'High velocity item - optimal location near shipping',
      status: 'pending',
      assignedTo: 'Mike Wilson',
      priority: 'high'
    },
    {
      id: 2,
      po: 'PO-2024-089',
      item: { sku: 'ELEC-002', name: 'Monitor 27" 4K', qty: 30 },
      suggestedLocation: 'Zone A → Aisle A1 → Rack A1-R2 → Shelf A1-R2-S1 → Bin A1-R2-S1-B2',
      reason: 'Medium velocity - mid-range storage',
      status: 'in-progress',
      assignedTo: 'Mike Wilson',
      priority: 'medium'
    }
  ];

  const handleScanBarcode = (receiptId, itemSku) => {
    // Simulate barcode scan
    console.log(`Scanned barcode for ${itemSku} in receipt ${receiptId}`);
  };

  const handleQCApprove = (receiptId, itemSku) => {
    // Handle QC approval
    console.log(`QC approved for ${itemSku} in receipt ${receiptId}`);
  };

  const handleQCReject = (receiptId, itemSku, reason) => {
    // Handle QC rejection
    console.log(`QC rejected for ${itemSku} in receipt ${receiptId}: ${reason}`);
  };

  return (
    <div className="space-y-6">
      {/* Stage Toggle */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Enhanced Receiving Operations</h2>
            <p className="text-sm text-gray-600 mt-1">Staged receiving → Quality Control → Directed Put-away</p>
          </div>
        </div>
        <div className="flex gap-2 bg-gray-100 p-1 rounded-lg">
          {[
            { id: 'staged', label: 'Staged Receiving', icon: Package, count: stagedReceipts.length },
            { id: 'qc', label: 'Quality Control', icon: CheckSquare, count: qcReceipts.length },
            { id: 'putaway', label: 'Directed Put-away', icon: MapPin, count: putawayTasks.length }
          ].map((stage) => {
            const Icon = stage.icon;
            return (
              <button
                key={stage.id}
                onClick={() => setActiveStage(stage.id)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-md transition-colors ${
                  activeStage === stage.id
                    ? 'bg-white text-orange-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{stage.label}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  activeStage === stage.id
                    ? 'bg-orange-100 text-orange-700'
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {stage.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Staged Receiving View */}
      {activeStage === 'staged' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {stagedReceipts.map((receipt) => (
            <motion.div
              key={receipt.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
            >
              <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-cyan-50">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{receipt.po}</h3>
                    <p className="text-sm text-gray-600 mt-1">{receipt.supplier}</p>
                  </div>
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                    Staged
                  </span>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Truck className="w-4 h-4 text-gray-500" />
                    <span className="text-gray-600">{receipt.dock}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gray-500" />
                    <span className="text-gray-600">{receipt.expectedTime}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-500" />
                    <span className="text-gray-600">{receipt.assignedTo}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-gray-500" />
                    <span className="text-gray-600">{receipt.items.length} items</span>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="space-y-3">
                  {receipt.items.map((item, index) => (
                    <div key={index} className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">{item.name}</h4>
                          <p className="text-sm text-gray-600">SKU: {item.sku}</p>
                        </div>
                        <span className="text-sm font-medium text-gray-700">
                          {item.received} / {item.qty}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-3">
                        <button
                          onClick={() => handleScanBarcode(receipt.id, item.sku)}
                          className="flex-1 px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center justify-center gap-2 text-sm font-medium"
                        >
                          <QrCode className="w-4 h-4" />
                          Scan to Receive
                        </button>
                        <button
                          onClick={() => {
                            alert(`Viewing details for ${item.name} (${item.sku}). This would show item specifications and history.`);
                          }}
                          className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => {
                      alert(`Completing receiving for ${receipt.po}. Moving all items to QC stage.`);
                    }}
                    className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2 font-medium"
                  >
                    <CheckCircle2 className="w-5 h-5" />
                    Complete Receiving & Move to QC
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Quality Control View */}
      {activeStage === 'qc' && (
        <div className="space-y-6">
          {qcReceipts.map((receipt) => (
            <motion.div
              key={receipt.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
            >
              <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-yellow-50 to-amber-50">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{receipt.po}</h3>
                    <p className="text-sm text-gray-600 mt-1">{receipt.supplier}</p>
                  </div>
                  <div className="text-right">
                    <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
                      QC In Progress
                    </span>
                    <p className="text-xs text-gray-600 mt-1">QC by: {receipt.qcBy}</p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {receipt.items.map((item, index) => (
                    <div key={index} className="p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">{item.name}</h4>
                          <p className="text-sm text-gray-600">SKU: {item.sku}</p>
                          <p className="text-sm text-gray-700 mt-1">Received: {item.received} units</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          item.qcStatus === 'approved' ? 'bg-green-100 text-green-700' :
                          item.qcStatus === 'rejected' ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {item.qcStatus}
                        </span>
                      </div>
                      {item.issues && item.issues.length > 0 && (
                        <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5" />
                            <div className="text-sm text-red-700">
                              {item.issues.map((issue, i) => (
                                <div key={i}>{issue}</div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleQCApprove(receipt.id, item.sku)}
                          className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2 text-sm font-medium"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          Approve
                        </button>
                        <button
                          onClick={() => handleQCReject(receipt.id, item.sku, 'Damaged goods')}
                          className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center gap-2 text-sm font-medium"
                        >
                          <XCircle className="w-4 h-4" />
                          Reject
                        </button>
                        <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                          <FileText className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <button
                    onClick={() => {
                      alert(`Completing QC for ${receipt.po}. Generating directed put-away tasks for approved items.`);
                    }}
                    className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center justify-center gap-2 font-medium"
                  >
                    <CheckCircle2 className="w-5 h-5" />
                    Complete QC & Generate Put-away Tasks
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Directed Put-away View */}
      {activeStage === 'putaway' && (
        <div className="space-y-6">
          {putawayTasks.map((task) => (
            <motion.div
              key={task.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
            >
              <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-green-50 to-emerald-50">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Put-away Task #{task.id}</h3>
                    <p className="text-sm text-gray-600 mt-1">PO: {task.po}</p>
                  </div>
                  <div className="text-right">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      task.status === 'completed' ? 'bg-green-100 text-green-700' :
                      task.status === 'in-progress' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {task.status}
                    </span>
                    <p className="text-xs text-gray-600 mt-1">Assigned: {task.assignedTo}</p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-semibold text-gray-900">{task.item.name}</h4>
                        <p className="text-sm text-gray-600">SKU: {task.item.sku}</p>
                      </div>
                      <span className="text-lg font-bold text-gray-900">{task.item.qty} units</span>
                    </div>
                  </div>
                  
                  <div className="p-4 border-2 border-orange-200 bg-orange-50 rounded-lg">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-orange-600 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-700 mb-1">Suggested Location:</p>
                        <p className="text-base font-bold text-orange-700">{task.suggestedLocation}</p>
                        <p className="text-xs text-gray-600 mt-2">
                          <span className="font-medium">Reason:</span> {task.reason}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button className="flex-1 px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center justify-center gap-2 font-medium">
                      <QrCode className="w-5 h-5" />
                      Scan Bin to Confirm
                    </button>
                    <button className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50">
                      <Search className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EnhancedReceiving;
